/**
 * Work-model hubs: the remote hub (`/jobs/remote`, fi `/tyopaikat/etatyo`, sv
 * `/lediga-jobb/distans`) and the hybrid hub (`/jobs/hybrid`, `/tyopaikat/hybridityo`,
 * `/lediga-jobb/hybridjobb`), plus their combinations with the existing location, category and
 * role markers (`/tyopaikat/etatyo/ammatti/sairaanhoitaja`, `/tyopaikat/hybridityo/alue/tampere_suomi`).
 *
 * A work-model page is a `remoteness` facet filter (Etätyö / Hybridityö and the en/sv
 * equivalents), nothing else. Until 2026-09-04 the remote hub was a pseudo-location
 * (`/alue/etätyö_suomi`, facet value "Etätyö - Suomi"), which made the H1 a facet value and split
 * remote jobs across three scope pages. See docs/knowledge-graph/01 "Remote and hybrid hubs".
 */
export type SupportedLocale = 'en' | 'fi' | 'sv';
export type WorkModelKey = 'remote' | 'hybrid';

export interface WorkModelDefinition {
  key: WorkModelKey;
  /** URL segment right after the jobs segment, per locale (ASCII, no diacritics). */
  segments: Record<SupportedLocale, string>;
  /** Canonical `remoteness` facet value per locale index. */
  facetValues: Record<SupportedLocale, string>;
}

export const WORK_MODEL_HUBS: readonly WorkModelDefinition[] = [
  {
    key: 'remote',
    segments: { en: 'remote', fi: 'etatyo', sv: 'distans' },
    facetValues: { en: 'Remote', fi: 'Etätyö', sv: 'Distans' },
  },
  {
    key: 'hybrid',
    segments: { en: 'hybrid', fi: 'hybridityo', sv: 'hybridjobb' },
    facetValues: { en: 'Hybrid', fi: 'Hybridityö', sv: 'Hybrid' },
  },
];

const SEGMENT_TO_KEY = new Map<string, WorkModelKey>();
for (const def of WORK_MODEL_HUBS) {
  for (const seg of Object.values(def.segments)) SEGMENT_TO_KEY.set(seg.toLowerCase(), def.key);
}
/** Every work-model URL segment, for path matchers and regexes. */
export const WORK_MODEL_SEGMENTS: readonly string[] = [...SEGMENT_TO_KEY.keys()];

export function getWorkModel(key: WorkModelKey): WorkModelDefinition {
  return WORK_MODEL_HUBS.find((d) => d.key === key)!;
}

/** Work model for a URL segment ("etatyo", "remote", "hybridjobb"), or null. */
export function workModelForSegment(segment: string | undefined | null): WorkModelKey | null {
  if (!segment) return null;
  let s = segment;
  try {
    s = decodeURIComponent(segment);
  } catch {
    /* keep raw */
  }
  return SEGMENT_TO_KEY.get(s.toLowerCase()) ?? null;
}

/** Work model whose facet value (any locale) equals `value`, case-insensitive, or null. */
export function workModelForFacetValue(value: string | undefined | null): WorkModelKey | null {
  const key = (value ?? '').trim().toLowerCase();
  if (!key) return null;
  for (const def of WORK_MODEL_HUBS) {
    if (Object.values(def.facetValues).some((v) => v.toLowerCase() === key)) return def.key;
  }
  return null;
}

/**
 * The work model a `remoteness` refinement encodes as a pretty path: exactly one value, and that
 * value is the locale's own facet value for a hub. Two values, or a value from another locale's
 * index, stay as query parameters.
 */
export function workModelForRemotenessRefinement(values: readonly string[] | undefined, locale: SupportedLocale): WorkModelKey | null {
  const list = (values ?? []).map((v) => String(v).trim()).filter(Boolean);
  if (list.length !== 1) return null;
  for (const def of WORK_MODEL_HUBS) {
    if (def.facetValues[locale].toLowerCase() === list[0].toLowerCase()) return def.key;
  }
  return null;
}

/** Localized URL segment of a work model. */
export function workModelSegment(key: WorkModelKey, locale: SupportedLocale): string {
  return getWorkModel(key).segments[locale];
}

/** Localized `remoteness` facet value of a work model. */
export function workModelFacetValue(key: WorkModelKey, locale: SupportedLocale): string {
  return getWorkModel(key).facetValues[locale];
}

/** True when the pathname carries a work-model segment directly after the jobs segment. */
export function isWorkModelPathname(pathname: string): boolean {
  return workModelFromPathname(pathname) !== null;
}

/** The work model encoded in a pathname, or null. */
export function workModelFromPathname(pathname: string): WorkModelKey | null {
  const segs = pathname.split('?')[0].split('/').filter(Boolean);
  let i = 0;
  if (segs[i] && ['en', 'fi', 'sv'].includes(segs[i].toLowerCase())) i++;
  if (!segs[i] || !['jobs', 'tyopaikat', 'lediga-jobb', 'jobb'].includes(segs[i].toLowerCase())) return null;
  return workModelForSegment(segs[i + 1]);
}
